import { useScrollReveal } from '../../../../hooks/useScrollReveal';

const features = [
  {
    icon: 'ri-briefcase-4-fill',
    title: 'Executive Airport Transfers',
    desc: 'Punctual, discreet door-to-door transfers between your office, hotel, or home and all three Charleston airports — with real-time flight tracking included.',
  },
  {
    icon: 'ri-user-star-fill',
    title: 'Total Privacy & Discretion',
    desc: 'Our chauffeurs are trained to maintain complete client confidentiality. We regularly serve C-suite executives, public figures, and high-profile professionals.',
  },
  {
    icon: 'ri-building-4-fill',
    title: 'All Business Districts Covered',
    desc: 'From downtown Charleston to Mount Pleasant, West Ashley, and North Charleston — we serve every business district in the region with precision and reliability.',
  },
  {
    icon: 'ri-file-list-3-fill',
    title: 'Clear Billing for Expenses',
    desc: 'Receive itemized receipts for every trip — clean, clear documentation that simplifies corporate expense reporting and business travel reimbursement.',
  },
  {
    icon: 'ri-group-fill',
    title: 'Group Corporate Travel',
    desc: 'Coordinating transportation for teams, delegations, and executive groups? We handle multi-vehicle corporate logistics seamlessly and professionally.',
  },
  {
    icon: 'ri-phone-fill',
    title: 'Priority 24/7 Availability',
    desc: 'Business travel doesn\'t follow a schedule — ours doesn\'t either. Early morning meetings, late-night arrivals, weekend events — we\'re always available.',
  },
];

export default function CorporateServiceBreakdown() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Text */}
            <div>
              <span className="text-gold text-xs tracking-widest uppercase font-semibold">Executive Transportation Specialists</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mt-3 mb-6 leading-tight">
                Corporate Travel<br />
                <span className="text-gold">At the Highest Level</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed text-sm mb-4">
                Business travel demands punctuality, discretion, and total reliability. Tec&apos;s Limo Service delivers executive-level ground transportation that meets the highest professional standards — every ride, every time.
              </p>
              <p className="text-neutral-500 leading-relaxed text-sm">
                From C-suite airport transfers to multi-day corporate retreats and client entertainment, we handle every transportation detail with the professionalism and discretion that high-level business requires.
              </p>
              <div className="mt-8 flex gap-4">
                <a href="#contact" className="bg-neutral-900 hover:bg-gold transition-colors text-white hover:text-black font-bold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  Book Corporate Transfer
                </a>
                <a href="tel:+18436422440" className="flex items-center gap-2 border-2 border-neutral-200 hover:border-gold text-neutral-700 hover:text-gold transition-colors font-semibold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
                  Call Now
                </a>
              </div>
            </div>
            {/* Image */}
            <div className="relative">
              <div className="w-full h-[420px] overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/e01bcced4fafac163363d84626667dfc.jpg"
                  alt="Corporate executive transportation Charleston SC"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-gold/30 pointer-events-none"></div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feat) => (
              <div key={feat.title} className="group p-6 border border-neutral-100 hover:border-gold/30 hover:bg-neutral-50 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-gold/10 group-hover:bg-gold/20 transition-colors mb-4">
                  <i className={`${feat.icon} text-gold text-xl`}></i>
                </div>
                <h4 className="font-semibold text-neutral-900 text-sm mb-2 group-hover:text-gold transition-colors">{feat.title}</h4>
                <p className="text-neutral-500 text-xs leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
